import React from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ISortOptions, ITheadSort, ListLayout } from '../../../../../../global/tables/table-list-layout';
import Text from '../../../../../../global/text/text';
import { ListDayItem } from '../../../../shared/list-day-item/list-day-item';
import { groupListByTime, isPreviousGroupDayDifferent } from '../../../../../../../infrastructure/utils/common.utils';
import { getSortedMeetingsByPresenterName } from '../../../../../../../infrastructure/utils/sorting.utils';
import { AttendeeColumn } from '../../../../../../global/attendee-column/attendee-column';
import MeetingDateTimeColumn from '../../../../../../global/meeting-date-time-column/meeting-date-time-column';
import { IMeetingDetailsDTO, IMeetingSaveDTO } from '../../../../../../../api/meetings.dao';

export interface IProps {
    createdByAIMeetingsList: IMeetingSaveDTO[];
    eventTimeZone: string;
}

const CreatedMeetingCoPilotTableLayout = (props: IProps) => {
    const { createdByAIMeetingsList, eventTimeZone } = props;
    const intl = useIntl();

    const meetingsList = createdByAIMeetingsList.map((meeting, idx) => ({
        ...meeting,
        meetingId: idx + 1,
    })) as IMeetingDetailsDTO[];

    const meetingsTimeGroups = groupListByTime(meetingsList);
    const theads: ITheadSort<string>[] = [
        {
            title: intl.formatMessage({
                id: 'oe.meeting.dateAndTime',
                description: 'Label text',
                defaultMessage: 'Date & Time'
            }),
            id: 'dataTime',
            width: '170px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.meeting.meetingPresenters',
                defaultMessage: 'Presenters',
                description: 'Label text',
            }),
            id: 'presenters',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.meeting.meetingParticipants',
                defaultMessage: 'Participants',
                description: 'Label text',
            }),
            id: 'participants',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.meeting.meetingHosts',
                defaultMessage: 'Hosts',
                description: 'Label text',
            }),
            id: 'hosts',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.meeting.meetingType',
                defaultMessage: 'Type',
                description: 'Label text',
            }),
            id: 'type',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.meeting.meetingTopic',
                defaultMessage: 'Topic',
                description: 'Label text',
            }),
            id: 'topic',
            width: '180px'
        },
    ];

    const sortOptions: ISortOptions<string> = {
        theads: theads
    };

    return (
        <section>
            <ListLayout
                sortOptions={sortOptions}
            >
                {meetingsTimeGroups.map((group: IMeetingDetailsDTO[], groupIndex: number) => {
                    const prevGroup = meetingsTimeGroups.length && meetingsTimeGroups[groupIndex - 1];
                    const isDayRendered = prevGroup ? isPreviousGroupDayDifferent(group, prevGroup, eventTimeZone) : false;

                    return group.length && (
                        <React.Fragment key={group[0].meetingId}>
                            {!isDayRendered && <ListDayItem
                                date={group[0].startDateTime}
                                eventTimezone={eventTimeZone}
                                colSpan={theads.length}
                                className='column-with-filters'
                            />}
                            {getSortedMeetingsByPresenterName<IMeetingDetailsDTO>(group).map((meeting: IMeetingDetailsDTO, meetingIndex) => {
                                const mergeWithPreviousRowClass = classnames({ 'merge-with-previous-row': !isDayRendered && meetingIndex === 0 });

                                return <tr key={meeting.meetingId}>
                                    <MeetingDateTimeColumn
                                        startDateTime={meeting.startDateTime}
                                        endDateTime={meeting.endDateTime}
                                        eventTimezone={eventTimeZone}
                                        isStartEndTimeRenderedInPreviousRow={meetingIndex !== 0}
                                        mergeWithPreviousRowClass={mergeWithPreviousRowClass}
                                    />
                                    <td className={classnames('hoverable', mergeWithPreviousRowClass)}>
                                        <AttendeeColumn
                                            organizations={meeting.presenters}
                                            withAttendeePopover={false}
                                            withRemove={false}
                                            withAddButton={false}
                                            intl={intl}
                                        />
                                    </td>
                                    <td className={classnames('hoverable', mergeWithPreviousRowClass)}>
                                        <AttendeeColumn
                                            organizations={meeting.participants}
                                            withAttendeePopover={false}
                                            withRemove={false}
                                            withAddButton={false}
                                            intl={intl}
                                        />
                                    </td>
                                    <td className={classnames('hoverable', mergeWithPreviousRowClass)}>
                                        <AttendeeColumn
                                            organizations={meeting.hosts}
                                            withAttendeePopover={false}
                                            withRemove={false}
                                            withAddButton={false}
                                            intl={intl}
                                        />
                                    </td>
                                    <Text className={classnames('hoverable', mergeWithPreviousRowClass)} tag='td' message={meeting.type} withEllipsis />
                                    <Text className={classnames('hoverable', mergeWithPreviousRowClass)} tag='td' message={meeting.topic} withEllipsis />
                                </tr>;
                            })}
                        </React.Fragment>
                    );
                })}
            </ListLayout>
        </section>
    );
};

export default CreatedMeetingCoPilotTableLayout;